import { catchError, Observable, retry, take, throwError, timeout } from 'rxjs';

export const typeOf = <T>(object: any, diskriminator: string): object is T => object ? diskriminator in object : false;


export const isNotEmpty = <T>(list: T[]): boolean => defined(list) && list.length > 0;

export const isEmpty = <T>(list: T[]): boolean => !list || list.length < 1;

export const push = <T>(list: T[], t: T): T[] => {
  const slice = list.slice();
  slice.push(t);
  return slice;
};

export const distict = <T>(list: T[], key: string): T[] => [...new Map(list.map(item => [item[key], item])).values()];

export const padLeft = (text: string, padChar: string, size: number): string =>
  (String(padChar).repeat(size) + text).substr((size * -1), size);

export const sortedMap = <ITEM, KEY>(items: ITEM[], keyFunktion: (t: ITEM) => KEY, sortAttribute: (i: ITEM) => number): Map<KEY, ITEM[]> => {
  const map: Map<KEY, ITEM[]> = new Map<KEY, ITEM[]>();

  items.forEach(item => {
    const items = map.get(keyFunktion(item));
    if (!items) {
      map.set(keyFunktion(item), [item]);
    } else {
      items.push(item);
      items.sort((itemA, itemB) => sortAttribute(itemA) - sortAttribute(itemB));
    }
  });
  return map;
};

export function stringToNumber(value: string): number {
  if (value === "-1") {
    return -1;
  }

  const s = value.toLowerCase().replace(/[^a-z]/g, "");

  let result = 0;

  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i) - 97;
    result += c / Math.pow(26, i + 1);
  }

  return result;
}

export const firstOrDefault = <T>(t: T[], defaultValue?: T): T => (t && t.length > 0 ? t[0] : defaultValue);

export const stringEmpty = (str: string): boolean => !str || str.length === 0;

export const toSet = <T>(t: T[]): T[] => t?.filter((value, index, self) => self.indexOf(value) === index);

export const flatMapValues = <K, V>(map: Map<K, V[]>): V[] => Array.from(map.values()).flatMap((v) => v);

export const innerJoin = <T>(left: T[], right: T[], key: (t: T) => unknown): T[] =>
  !left || !right ? undefined : left.filter((l) => right.find((r) => key(l) === key(r)));

export const leftOuterJoin = <T>(left: T[], right: T[], key: (t: T) => unknown): T[] =>
  !left || !right ? undefined : left.filter((l) => !right.find((r) => key(l) === key(r)));

export const stringMax = (s: string, max: number): string => (s?.length <= max ? s : s?.substring(0, max - 3) + '...');

export const toMap = <T, K>(t: T[], key: (t: T) => K): Map<K, T> => t ? new Map(t.map(obj => [key(obj), obj])) : undefined;

export const groupBy = <T, K>(t: T[], key: (t: T) => K): Map<K, T[]> => {
  if (!t) return undefined;
  const map = new Map<K, T[]>();
  t.forEach(obj => {
    const k = key(obj);
    if (!map.has(k)) {
      map.set(k, [obj]);
    } else {
      map.get(k).push(obj);
    }
  });
  return map;
};

export const defined = <T>(t: T): boolean => !!t;

export const makeBooleanHashKey = (...bools: boolean[]): string => {
  return bools.map(b => (b ? 1 : 0)).join('_');
};

export const stringToMap = (str: string): Map<string, string> =>
  str ? new Map<string, string>(JSON.parse(str)) : undefined;

export const mapToString = (map: Map<string, string>): string =>
  JSON.stringify(Array.from(map.entries()));

export const timeString = (timeStamp: string): string => {
  const date = new Date(timeStamp + 'Z');
  return isValidDate(date) ? date.toLocaleTimeString() : undefined;
};


export const duration = (start: string, finish: string): string => {
  const startDate = new Date(start);
  const finishDate = new Date(finish);

  if (isValidDate(startDate) && isValidDate(finishDate)) {
    return formatTimeDifference(startDate, finishDate);
  } else {
    return '';
  }
};

export const formatDate = (date: Date): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are zero-indexed in JavaScript
  const day = pad(date.getDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const formatDateUTC = (date: Date): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1); // Months are zero-indexed in JavaScript
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
};

export const formatTimeDifference = (startDate: Date, endDate: Date): string => {
  const diffInMilliseconds = endDate.getTime() - startDate.getTime();
  const hours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60)) + (hours * 60);
  const seconds = Math.floor((diffInMilliseconds % (1000 * 60)) / 1000);
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');
  return `${formattedMinutes}:${formattedSeconds}`;
};

export const pushToMap = <K, V>(map: Map<K, V[]>, key: K, value: V): void => {
  if (!map.has(key)) {
    map.set(key, [value]);
  } else {
    const existingValues = map.get(key);
    existingValues.push(value);
    map.set(key,existingValues);
  }
};

export const formatDateSvSE = (start: string, end: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  };
  const sv = 'sv-SE';
  const format = (dateStr: string) => {
    const date = new Date(dateStr);
    const parts = date.toLocaleDateString(sv, options).split(' ');
    // Replace full weekday with first 3 chars
    parts[0] = parts[0].slice(0, 3);
    return parts.join(' ');
  };
  return `${format(start)} - ${format(end)}`;
};


const isValidDate = (d: Date) => !!d && !isNaN(d.getTime());

export function withRetryAndTimeout<T>(
  endpointFn: (...args: any[]) => Observable<T>,
  endpointArgs: any[],
  timeoutMs = 5000,
  retryCount = 3,
  retryDelay = 2500,
): Observable<T> {
  return endpointFn(...endpointArgs).pipe(
    timeout(timeoutMs),
    retry({ count: retryCount, delay: retryDelay }),
    catchError(error => {
      console.error('Request failed after retries:', error);
      return throwError(() => error);
    }),
    take(1)
  );
}

